import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, Mail, Lock, ArrowRight, Loader2, ShieldCheck, Globe2, Clock3 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const LOGO = '/gerizim-logo.png';

const SpreadsheetLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast({ title: 'Welcome back', description: 'Signed in successfully' });
      navigate('/erp');
    } catch {
      toast({ title: 'Sign in failed', description: 'Invalid email or password', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#FFFFFF' }}>
      {/* ===== LEFT — Navy brand panel ===== */}
      <div
        className="hidden lg:flex flex-col justify-between w-[52%] relative overflow-hidden p-12"
        style={{ background: 'linear-gradient(160deg, #071429 0%, #0A1F3C 55%, #0E2A4E 100%)' }}
      >
        {/* Gold accents */}
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.18), transparent 65%)', filter: 'blur(40px)' }} />
        <div className="absolute -bottom-40 -left-24 w-[30rem] h-[30rem] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.10), transparent 60%)', filter: 'blur(60px)' }} />
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.05]"
          style={{
            backgroundImage: 'linear-gradient(rgba(212,175,55,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.6) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />

        {/* Brand top */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative flex items-center gap-3"
        >
          <div>
            <p className="text-white font-bold tracking-[0.18em] text-lg">GERIZIM</p>
            <p className="text-[10px] tracking-[0.22em] uppercase" style={{ color: '#C9A227' }}>Customs Clearance</p>
          </div>
        </motion.div>

        {/* Center statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="relative max-w-lg"
        >
          <div className="w-40 h-40 rounded-[2.2rem] bg-white flex items-center justify-center mb-8" style={{ boxShadow: '0 24px 60px -16px rgba(0,0,0,0.5), 0 0 0 1px rgba(212,175,55,0.35)' }}>
            <img src={LOGO} alt="Gerizim Customs Clearance" className="w-[8.5rem] h-[8.5rem] object-contain" />
          </div>
          <div className="h-[2px] w-16 mb-8" style={{ background: 'linear-gradient(90deg, #D4AF37, transparent)' }} />
          <h1 className="text-4xl xl:text-5xl font-bold leading-[1.12] text-white" style={{ letterSpacing: '-0.02em' }}>
            Every shipment,<br />
            every payment,<br />
            <span style={{ color: '#D4AF37' }}>under control.</span>
          </h1>
          <p className="mt-6 text-[15px] leading-relaxed" style={{ color: '#93A7C4' }}>
            The Gerizim operations platform — customs clearance, freight tracking and customer balances in one place.
          </p>
        </motion.div>

        {/* Bottom trust row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="relative flex items-center gap-8"
        >
          {[
            { icon: ShieldCheck, label: 'Secure access' },
            { icon: Globe2, label: 'Import · Export' },
            { icon: Clock3, label: 'Live tracking' },
          ].map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-2">
              <Icon size={14} style={{ color: '#D4AF37' }} />
              <span className="text-xs" style={{ color: '#93A7C4' }}>{label}</span>
            </div>
          ))}
        </motion.div>
      </div>

      {/* ===== RIGHT — Clean white form ===== */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 relative">
        <div className="w-full max-w-[400px]">
          {/* Mobile logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mb-10 text-center lg:text-left"
          >
            <img src={LOGO} alt="Gerizim" className="h-40 w-auto mx-auto lg:mx-0 lg:hidden" />
            <h2 className="mt-4 lg:mt-0 text-3xl font-bold" style={{ color: '#0A1F3C', letterSpacing: '-0.02em' }}>
              Welcome back
            </h2>
            <p className="mt-2 text-sm" style={{ color: '#5D6F88' }}>
              Sign in to the Gerizim portal
            </p>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            className="space-y-5"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div>
              <label className="block mb-2 text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: '#0A1F3C' }}>
                Email Address
              </label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: '#93A0B4' }} />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@gerizim.com"
                  required
                  data-testid="email-input"
                  className="w-full pl-10 pr-3 h-12 rounded-xl text-sm outline-none transition-all"
                  style={{
                    background: '#F4F6F9',
                    color: '#0A1F3C',
                    border: '1px solid #E1E6ED',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#C9A227'; e.target.style.boxShadow = '0 0 0 4px rgba(201,162,39,0.12)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#E1E6ED'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>

            <div>
              <label className="block mb-2 text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: '#0A1F3C' }}>
                Password
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: '#93A0B4' }} />
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                  className="w-full pl-10 pr-3 h-12 rounded-xl text-sm outline-none transition-all"
                  style={{
                    background: '#F4F6F9',
                    color: '#0A1F3C',
                    border: '1px solid #E1E6ED',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#C9A227'; e.target.style.boxShadow = '0 0 0 4px rgba(201,162,39,0.12)'; }}
                  onBlur={(e) => { e.target.style.borderColor = '#E1E6ED'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            </div>

            <motion.button
              type="submit"
              disabled={loading}
              data-testid="login-button"
              whileHover={{ scale: loading ? 1 : 1.01, y: loading ? 0 : -1 }}
              whileTap={{ scale: loading ? 1 : 0.99 }}
              className="w-full h-12 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-70 transition-shadow"
              style={{
                background: 'linear-gradient(135deg, #0A1F3C 0%, #14314F 100%)',
                color: '#FFFFFF',
                boxShadow: '0 10px 26px -8px rgba(10,31,60,0.55), inset 0 1px 0 rgba(255,255,255,0.12)',
                border: '1px solid rgba(212,175,55,0.35)',
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Signing in…
                </>
              ) : (
                <>
                  <LogIn size={15} style={{ color: '#D4AF37' }} /> Sign In <ArrowRight size={14} style={{ color: '#D4AF37' }} />
                </>
              )}
            </motion.button>

            <div className="pt-5 flex items-center justify-between text-xs" style={{ borderTop: '1px solid #ECEFF4' }}>
              <span style={{ color: '#93A0B4' }}>New employee?</span>
              <Link
                to="/register"
                className="font-semibold flex items-center gap-1 transition-colors"
                style={{ color: '#A9861D' }}
              >
                Create account <ArrowRight size={12} />
              </Link>
            </div>
          </motion.form>

          <motion.p
            className="text-center lg:text-left text-[11px] mt-10"
            style={{ color: '#B7C0CC' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            Gerizim Customs Clearance © {new Date().getFullYear()} · Secure internal access
          </motion.p>
        </div>
      </div>
    </div>
  );
};

export default SpreadsheetLogin;
